import { makeAutoObservable, runInAction } from 'mobx';

import api from '../api';
import { Dota2Hero } from '../types/heroes';
import { IMatch, MatchStatus } from '../types/match';
import { IPlayerInMatch, ITeams } from '../types/player';
import { IStatus } from '../types/status';

// На бэкенд игроков отдаём ссылкой (id), а не populated-объектом
const serializeTeam = (team: IPlayerInMatch[]) =>
    team.map((slot) => ({ ...slot, player: slot.player?._id ?? null }));

class Matches {
    matches: IMatch[] = [];
    matchesFetchStatus: IStatus = 'idle';

    currentMatch: IMatch | null = null;
    currentMatchFetchStatus: IStatus = 'idle';

    heroes: Dota2Hero[] = [];
    heroesFetchStatus: IStatus = 'idle';

    constructor() {
        makeAutoObservable(this);
    }

    get heroesById() {
        return new Map(this.heroes.map((hero) => [hero.id, hero]));
    }

    getHero(id: number | undefined) {
        return id === undefined ? undefined : this.heroesById.get(id);
    }

    /** Создаёт матч и возвращает его id. */
    async create(teams: ITeams) {
        const { data } = await api.put<IMatch['_id']>('matches', {
            teams,
            status: MatchStatus.uncommited,
        });
        return data;
    }

    async getAll() {
        this.matchesFetchStatus = 'pending';
        try {
            const { data } = await api.get<IMatch[]>('matches');
            runInAction(() => {
                this.matches = data;
                this.matchesFetchStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.matchesFetchStatus = 'rejected';
            });
        }
    }

    async getById(id: string) {
        if (this.currentMatch?._id !== id) {
            this.currentMatch = null;
        }
        this.currentMatchFetchStatus = 'pending';
        try {
            const { data } = await api.get<IMatch>('matches/' + id);
            runInAction(() => {
                this.currentMatch = data;
                this.currentMatchFetchStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.currentMatchFetchStatus = 'rejected';
            });
        }
    }

    /** Сохраняет ручные правки матча. Бросает ошибку при неудаче. */
    async save(match: IMatch) {
        await api.patch<IMatch>('/matches/' + match._id, {
            teamRadiant: serializeTeam(match.teamRadiant),
            teamDire: serializeTeam(match.teamDire),
            radiant_win: match.radiant_win,
            radiantKills: match.radiantKills,
            direKills: match.direKills,
        });
        // PATCH отдаёт матч без populate игроков — перечитываем целиком
        await this.getById(match._id);
    }

    /** Подтягивает данные матча из Steam по его dota match id. */
    async fetchMatchFromSteam(id: IMatch['_id'], matchId: string) {
        const { data } = await api.post<IMatch>('/matches/' + id, {
            matchId,
        });
        runInAction(() => {
            this.currentMatch = data;
        });
        return data;
    }

    async fetchHeroes() {
        if (this.heroesFetchStatus === 'pending') return;
        this.heroesFetchStatus = 'pending';
        try {
            const { data } = await api.get<Dota2Hero[]>('/heroes');
            runInAction(() => {
                this.heroes = [...data].sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                this.heroesFetchStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.heroesFetchStatus = 'rejected';
            });
        }
    }
}

const matchesStore = new Matches();
export default matchesStore;
